import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from 'reselect';
import withRouter from '../Components/Common/withRouter';

// Components
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import SyncStatusBanner from '../Components/Common/SyncStatusBanner';
import FloatingChat from '../Components/Common/FloatingChat';
import TourOverlay from '../Components/Common/TourOverlay';
import CountrySelectionModal from '../Components/Common/CountrySelectionModal';

// Auth & API
import { useAuth } from '../contexts/AuthContext';
import { profileApi } from '../services/api';

// Actions
import {
    changeLayout,
    changeSidebarTheme,
    changeLayoutMode,
    changeLayoutWidth,
    changeLayoutPosition,
    changeTopbarTheme,
    changeLeftsidebarSizeType,
    changeLeftsidebarViewType,
    changeSidebarImageType,
    changeSidebarVisibility
} from "../slices/thunks";

// Pages where FloatingChat should be hidden (admin uses center chat instead)
const HIDE_FLOATING_CHAT_PATHS = ['/apps-chat', '/apps-mailbox'];

const Layout = (props : any) => {
    const [headerClass, setHeaderClass] = useState("");
    const [showCountryModal, setShowCountryModal] = useState(false);
    const [countryChecked, setCountryChecked] = useState(false);
    const location = useLocation();
    const { user, updateUser } = useAuth();

    // Check if FloatingChat should be hidden on current page
    const shouldHideFloatingChat = HIDE_FLOATING_CHAT_PATHS.some(path =>
        location.pathname.startsWith(path)
    );
    const dispatch : any = useDispatch();

    // Check if user has country set (for VAT purposes)
    useEffect(() => {
        const checkCountry = async () => {
            // Skip for admin users or if already checked
            if (!user || user.is_admin || user.role === 'admin' || countryChecked) return;

            // If user already has country in local state, skip
            if (user.country) {
                setCountryChecked(true);
                return;
            }

            try {
                // Fetch profile to check country
                const response = await profileApi.getProfile();
                if (response?.success && response?.profile) {
                    const country = response.profile.country;
                    if (!country) {
                        // No country set, show modal
                        setShowCountryModal(true);
                    } else {
                        // Update local user state with country
                        updateUser({ country });
                    }
                }
            } catch (error) {
                console.error('Failed to check country:', error);
            } finally {
                setCountryChecked(true);
            }
        };

        checkCountry();
    }, [user, countryChecked, updateUser]);

    const selectLayoutState = (state : any) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutType: layout.layoutType,
            leftSidebarType: layout.leftSidebarType,
            layoutModeType: layout.layoutModeType,
            layoutWidthType: layout.layoutWidthType,
            layoutPositionType: layout.layoutPositionType,
            topbarThemeType: layout.topbarThemeType,
            leftsidbarSizeType: layout.leftsidbarSizeType,
            leftSidebarViewType: layout.leftSidebarViewType,
            leftSidebarImageType: layout.leftSidebarImageType,
            preloader: layout.preloader,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
        })
    );
    // Inside your component
    const {
        layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype
    } = useSelector(selectLayoutProperties);

    /*
    layout settings
    */
    useEffect(() => {
        if (
            layoutType ||
            leftSidebarType ||
            layoutModeType ||
            layoutWidthType ||
            layoutPositionType ||
            topbarThemeType ||
            leftsidbarSizeType ||
            leftSidebarViewType ||
            leftSidebarImageType ||
            sidebarVisibilitytype
        ) {
            window.dispatchEvent(new Event('resize'));
            dispatch(changeLeftsidebarViewType(leftSidebarViewType));
            dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
            dispatch(changeSidebarTheme(leftSidebarType));
            dispatch(changeLayoutMode(layoutModeType));
            dispatch(changeLayoutWidth(layoutWidthType));
            dispatch(changeLayoutPosition(layoutPositionType));
            dispatch(changeTopbarTheme(topbarThemeType));
            dispatch(changeLayout(layoutType));
            dispatch(changeSidebarImageType(leftSidebarImageType));
            dispatch(changeSidebarVisibility(sidebarVisibilitytype));
        }
    }, [layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype,
        dispatch]);
    /*
    call dark/light mode
    */
    const onChangeLayoutMode = (value : any) => {
        if (changeLayoutMode) {
            dispatch(changeLayoutMode(value));
        }
    };

    // Handle country selection from modal
    const handleCountrySelected = (country: string) => {
        updateUser({ country });
        setShowCountryModal(false);
    };

    // class add remove in header 
    useEffect(() => {
        window.addEventListener("scroll", scrollNavigation, true);
    });

    function scrollNavigation() {
        var scrollup = document.documentElement.scrollTop;
        if (scrollup > 50) {
            setHeaderClass("topbar-shadow");
        } else {
            setHeaderClass("");
        }
    }

    useEffect(() => {
        const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement;
        if (sidebarVisibilitytype === 'show' || layoutType === "vertical" || layoutType === "twocolumn") {
            humberIcon.classList.remove('open');
        } else {
            humberIcon && humberIcon.classList.add('open');
        }
    }, [sidebarVisibilitytype, layoutType]);

    return (
        <React.Fragment>
            <div id="layout-wrapper">
                <Header
                    headerClass={headerClass}
                    layoutModeType={layoutModeType}
                    onChangeLayoutMode={onChangeLayoutMode} />
                <Sidebar
                    layoutType={layoutType}
                />
                <div className="main-content">
                    {props.children}
                    <Footer />
                </div>
            </div>
            {/* Sync Status Notification Banner */}
            <SyncStatusBanner />
            {/* Floating Chat Widget - hidden on admin chat pages */}
            {!shouldHideFloatingChat && <FloatingChat />}
            {/* Onboarding Tour Overlay */}
            <TourOverlay />
            {/* Country Selection Modal - shows on first login if country not set */}
            <CountrySelectionModal
                isOpen={showCountryModal}
                onCountrySelected={handleCountrySelected}
            />
        </React.Fragment>

    );
};

Layout.propTypes = {
    children: PropTypes.object,
};

export default withRouter(Layout);